import { post } from '../api.js';
import { rewrapExistingMasterKey, deriveAndStoreKeys, deriveRecoveryKeys, unwrapMasterKeyFromRecovery, wrapMasterKeyForRecovery, checkHIBP, generateBip39Mnemonic, validateBip39Mnemonic, wipe } from '../crypto.js';
import { navigate } from '../router.js';
import { lucideIcons, esc } from '../app.js';
import { setToken } from '../auth-store.js';

export function renderRecover(container) {
  let email = null;
  let recoveryData = null;
  let oldRecoveryAuthHex = null;
  let newMnemonic = null;
  let newPassword = null;

  renderStep1();

  function renderStep1() {
    container.innerHTML = `
      <div class="login-card">
        <div class="logo">
          <svg viewBox="0 0 32 32" width="48" height="48"><defs><linearGradient id="bk-recover" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-recover)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-recover)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-recover)" stroke-width="1.5" stroke-linecap="round"/></svg>
          <div class="brand">Recover Vault</div>
        </div>
        <p style="color:var(--text-secondary);margin-bottom:1.5rem;text-align:center">
          Enter your email to begin recovery.
        </p>
        <div id="recover-msg"></div>
        <form id="recover-email-form">
          <div class="field"><label>Email</label><input type="email" name="email" required></div>
          <button type="submit" class="btn btn-primary" style="margin-top:8px">Continue</button>
        </form>
        <p style="text-align:center;margin-top:16px;font-size:13px">
          <a href="#/login" style="color:var(--text-secondary)">Back to sign in</a>
        </p>
      </div>
    `;
    lucideIcons();

    document.getElementById('recover-email-form').onsubmit = async (e) => {
      e.preventDefault();
      const msg = document.getElementById('recover-msg');
      const btn = e.target.querySelector('button');
      email = e.target.email.value.trim().toLowerCase();
      btn.disabled = true;
      msg.innerHTML = '';

      try {
        recoveryData = await post('/auth/recovery-params', { email });
        renderStep2();
      } catch (err) {
        msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        btn.disabled = false;
      }
    };
  }

  function renderStep2() {
    const inputs = Array.from({ length: 12 }, (_, i) =>
      `<div class="field" style="margin-bottom:4px"><label style="font-size:12px">Word ${i + 1}</label><input type="text" name="w${i}" required autocomplete="off" autocapitalize="none" style="font-family:monospace"></div>`
    ).join('');

    container.innerHTML = `
      <div class="login-card" style="max-width:520px">
        <div class="logo">
          <svg viewBox="0 0 32 32" width="48" height="48"><defs><linearGradient id="bk-recover" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-recover)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-recover)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-recover)" stroke-width="1.5" stroke-linecap="round"/></svg>
          <div class="brand">Enter Recovery Words</div>
        </div>
        <div id="recover-msg"></div>
        <form id="recover-words-form">
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px">${inputs}</div>
          <button type="submit" class="btn btn-primary" style="margin-top:12px;width:100%">Verify &amp; Decrypt</button>
        </form>
        <p style="text-align:center;margin-top:16px;font-size:13px">
          <a href="#" id="back-to-email" style="color:var(--text-secondary)">Back</a>
        </p>
      </div>
    `;
    lucideIcons();

    document.getElementById('back-to-email').onclick = (e) => { e.preventDefault(); renderStep1(); };

    document.getElementById('recover-words-form').onsubmit = async (e) => {
      e.preventDefault();
      const msg = document.getElementById('recover-msg');
      const btn = e.target.querySelector('button');
      msg.innerHTML = '';

      const words = [];
      for (let i = 0; i < 12; i++) {
        words.push(e.target[`w${i}`].value.trim().toLowerCase());
      }
      const mnemonic = words.join(' ');

      const valid = await validateBip39Mnemonic(mnemonic);
      if (!valid) {
        msg.innerHTML = '<div class="msg msg-error">Invalid recovery words. Check spelling and order.</div>';
        return;
      }

      btn.disabled = true;
      btn.textContent = 'Decrypting...';

      let recovery;
      try {
        recovery = await deriveRecoveryKeys(mnemonic, recoveryData.recovery_version || 1);
        oldRecoveryAuthHex = Array.from(recovery.authKey).map(b => b.toString(16).padStart(2, '0')).join('');

        unwrapMasterKeyFromRecovery(
          recovery.wrappingKey,
          recoveryData.recovery_wrapped_master_key,
          recoveryData.encrypted_private_key,
          recoveryData.public_key,
          recoveryData.recovery_wrapped_master_key_format_version,
          recoveryData.encrypted_private_key_format_version,
        );

        renderStep3();
      } catch (err) {
        msg.innerHTML = '<div class="msg msg-error">Wrong recovery words or decryption failed.</div>';
        btn.disabled = false;
        btn.textContent = 'Verify & Decrypt';
      } finally {
        if (recovery) wipe(recovery.wrappingKey, recovery.authKey);
      }
    };
  }

  function renderStep3() {
    container.innerHTML = `
      <div class="login-card">
        <div class="logo">
          <svg viewBox="0 0 32 32" width="48" height="48"><defs><linearGradient id="bk-recover" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-recover)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-recover)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-recover)" stroke-width="1.5" stroke-linecap="round"/></svg>
          <div class="brand">Set New Passphrase</div>
        </div>
        <p style="color:var(--text-secondary);margin-bottom:1.5rem;text-align:center">
          Your vault has been decrypted. Set a new passphrase to re-secure it.
        </p>
        <div id="recover-msg"></div>
        <form id="new-pw-form">
          <div class="field"><label>New Passphrase</label><input type="password" name="password" required minlength="16" placeholder="e.g. correct horse battery staple"></div>
          <p style="font-size:12px;color:var(--text-secondary);margin:-4px 0 8px">Use 4+ random words. Minimum 16 characters.</p>
          <div class="field"><label>Confirm Passphrase</label><input type="password" name="confirm" required></div>
          <div id="hibp-warning" style="display:none">
            <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-secondary);margin-bottom:8px">
              <input type="checkbox" id="accept-risk"> Breach check unavailable. I accept the risk.
            </label>
          </div>
          <button type="submit" class="btn btn-primary" style="margin-top:8px">Continue</button>
        </form>
        <p style="text-align:center;margin-top:16px;font-size:13px">
          <a href="#" id="back-to-words" style="color:var(--text-secondary)">Back</a>
        </p>
      </div>
    `;
    lucideIcons();

    document.getElementById('back-to-words').onclick = (e) => { e.preventDefault(); renderStep2(); };

    document.getElementById('new-pw-form').onsubmit = async (e) => {
      e.preventDefault();
      const msg = document.getElementById('recover-msg');
      const btn = e.target.querySelector('button');
      const password = e.target.password.value;
      const confirm = e.target.confirm.value;
      msg.innerHTML = '';

      if (password.length < 16) {
        msg.innerHTML = '<div class="msg msg-error">Passphrase must be at least 16 characters.</div>';
        return;
      }
      if (password !== confirm) {
        msg.innerHTML = '<div class="msg msg-error">Passphrases do not match.</div>';
        return;
      }

      btn.disabled = true;
      btn.textContent = 'Checking...';

      const hibp = await checkHIBP(password);
      if (hibp.breached) {
        msg.innerHTML = `<div class="msg msg-error">This password appeared in ${hibp.count.toLocaleString()} data breaches. Choose a different one.</div>`;
        btn.disabled = false;
        btn.textContent = 'Continue';
        return;
      }
      if (hibp.error) {
        const warning = document.getElementById('hibp-warning');
        warning.style.display = '';
        if (!document.getElementById('accept-risk').checked) {
          msg.innerHTML = '<div class="msg msg-warning">Could not check breach database. Check the box below to proceed anyway.</div>';
          btn.disabled = false;
          btn.textContent = 'Continue';
          return;
        }
      }

      newPassword = password;
      newMnemonic = await generateBip39Mnemonic();
      renderStep4();
    };
  }

  function renderStep4() {
    const words = newMnemonic.split(' ');
    const grid = words.map((w, i) => `<div class="recovery-word"><span class="recovery-num">${i + 1}.</span> ${esc(w)}</div>`).join('');
    const positions = [];
    while (positions.length < 3) {
      const p = Math.floor(Math.random() * 12);
      if (!positions.includes(p)) positions.push(p);
    }
    positions.sort((a, b) => a - b);

    container.innerHTML = `
      <div class="login-card" style="max-width:520px">
        <div class="logo">
          <svg viewBox="0 0 32 32" width="48" height="48"><defs><linearGradient id="bk-recover" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-recover)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-recover)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-recover)" stroke-width="1.5" stroke-linecap="round"/></svg>
          <div class="brand">New Recovery Words</div>
        </div>
        <p style="color:var(--text-secondary);margin-bottom:1rem;text-align:center">
          Your old recovery words are no longer valid. Save these new words.
        </p>
        <div class="recovery-grid">${grid}</div>
        <div style="display:flex;gap:8px;margin-top:16px">
          <button class="btn btn-secondary btn-sm" id="copy-words"><i data-lucide="copy" style="width:14px;height:14px"></i> Copy</button>
        </div>
        <div style="margin-top:20px;width:100%">
          <div id="recover-msg"></div>
          <form id="confirm-form">
            <p style="font-size:13px;color:var(--text-secondary);margin-bottom:8px">Confirm 3 words:</p>
            ${positions.map(p => `<div class="field"><label>Word #${p + 1}</label><input type="text" name="word${p}" required autocomplete="off" autocapitalize="none"></div>`).join('')}
            <button type="submit" class="btn btn-primary" style="width:100%;margin-top:8px">Complete Recovery</button>
            <button type="button" class="btn btn-secondary" style="width:100%;margin-top:8px" id="back-to-pw">Back — Generate New Words</button>
          </form>
        </div>
      </div>
    `;
    lucideIcons();

    document.getElementById('copy-words').onclick = () => {
      navigator.clipboard.writeText(newMnemonic).then(() => {
        const btn = document.getElementById('copy-words');
        btn.textContent = 'Copied!';
        setTimeout(() => { navigator.clipboard.writeText(''); }, 60000);
        setTimeout(() => { btn.innerHTML = '<i data-lucide="copy" style="width:14px;height:14px"></i> Copy'; lucideIcons(); }, 2000);
      });
    };

    document.getElementById('back-to-pw').onclick = () => {
      newMnemonic = null;
      renderStep3();
    };

    document.getElementById('confirm-form').onsubmit = async (e) => {
      e.preventDefault();
      const msg = document.getElementById('recover-msg');
      const btn = e.target.querySelector('button');
      msg.innerHTML = '';

      for (const p of positions) {
        const input = e.target[`word${p}`].value.trim().toLowerCase();
        if (input !== words[p]) {
          msg.innerHTML = '<div class="msg msg-error">One or more words are incorrect.</div>';
          return;
        }
      }

      btn.disabled = true;
      btn.textContent = 'Recovering...';

      try {
        // Re-wrap existing master key with new password (preserves vault data)
        const keys = rewrapExistingMasterKey(newPassword);
        const newAuthKeyHex = keys.auth_key;

        // Derive new recovery keys
        const newRecovery = await deriveRecoveryKeys(newMnemonic);
        const newRecoveryWrapped = wrapMasterKeyForRecovery(newRecovery.wrappingKey);
        const newRecoveryAuthHex = Array.from(newRecovery.authKey).map(b => b.toString(16).padStart(2, '0')).join('');
        wipe(newRecovery.wrappingKey, newRecovery.authKey);

        const auth = await post('/auth/recover', {
          email,
          recovery_auth_key: oldRecoveryAuthHex,
          new_auth_key: newAuthKeyHex,
          new_client_salt: keys.client_salt,
          new_encrypted_master_key: keys.encrypted_master_key,
          new_encrypted_private_key: keys.encrypted_private_key,
          new_public_key: keys.public_key,
          new_recovery_wrapped_master_key: Array.from(newRecoveryWrapped),
          new_recovery_auth_key: newRecoveryAuthHex,
        });

        setToken(auth.token);
        deriveAndStoreKeys(
          newPassword,
          keys.client_salt,
          keys.encrypted_private_key,
          keys.public_key,
          keys.encrypted_master_key,
          keys.encrypted_master_key_format_version,
          keys.encrypted_private_key_format_version,
        );

        navigate('#/items');
      } catch (err) {
        msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        btn.disabled = false;
        btn.textContent = 'Complete Recovery';
      }
    };
  }
}
